import { auth, firestore } from "@/firebase";
import { getUserDefaultRecordId } from "@/models/user";
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { Atom, atom } from "jotai";
import { observeFirestoreQueryAtom } from "./atoms/observeFirestoreQueryAtom";
import { CreateEntityOptions, createEntity } from "./entity";
import {
  CombinedQueryConstraint,
  Entity,
  EntityQueryResults,
  EntityRecord,
  HydratedEntity,
  Model,
  ModelOptions,
  PartialEntityRecord,
  QueryComposite,
  QueryOptions,
  SerializedQueryConstraint,
} from "./types";

export const createModel = <T extends EntityRecord>(
  options: ModelOptions<T>,
): Model<T> => {
  const {
    labels,
    collectionPath,
    getCreatorId = () => auth.currentUser?.uid || "UnknownModelUser",
    getDefaultRecord,
    version,
  } = options;
  const collectionRef = collection(firestore, collectionPath);
  const atomCache = new Map<string, Atom<EntityQueryResults<T>>>();

  const getQueryComposite = (
    constraints: CombinedQueryConstraint[] = [],
  ): QueryComposite => {
    const serializedConstraints: SerializedQueryConstraint[] = constraints.map(
      (constraint): SerializedQueryConstraint =>
        Array.isArray(constraint)
          ? constraint
          : [constraint, constraint.type + new Date().getUTCMilliseconds()],
    );

    const hash =
      collectionPath +
      "," +
      serializedConstraints
        .map(
          ([constraint, id], index) => index + ":" + constraint.type + ":" + id,
        )
        .join(",");

    const q = query(
      collectionRef,
      ...serializedConstraints.map(([constraint]) => constraint),
    );

    return { hash, query: q };
  };

  const getBaseQueryConstraints = (
    constraints: CombinedQueryConstraint[] = [],
  ): CombinedQueryConstraint[] => {
    //const creatorId = getCreatorId();
    const allConstraints: CombinedQueryConstraint[] = [
      ///[where("creatorId", "==", creatorId), "creatorId==" + creatorId],
      [where("version", "==", version), "version==" + version],
      [where("status", "==", "published"), "status==published"],
      ...constraints,
    ];

    return allConstraints;
  };

  const getBaseQueryComposite = (
    constraints: CombinedQueryConstraint[] = [],
  ): QueryComposite => {
    return getQueryComposite(getBaseQueryConstraints(constraints));
  };

  const getEntities = async (
    queryOptions?: QueryOptions,
  ): Promise<Entity<T>[]> => {
    const composite = getBaseQueryComposite(queryOptions?.constraints || []);
    const snapshot = await getDocs(composite.query);

    return snapshot.docs.map((snapshot) =>
      createEntity<T>({
        model,
        id: snapshot.id,
        data: snapshot.data() as T,
      }),
    );
  };

  const getObserveEntitiesAtom = (queryOptions?: QueryOptions) => {
    const constraints = [
      ...(queryOptions?.constraints || []),
      ...getBaseQueryConstraints(),
    ];

    const composite = getQueryComposite(constraints);

    let cachedAtom = atomCache.get(composite.hash);

    if (!cachedAtom) {
      const atm = observeFirestoreQueryAtom<T>(composite.query, composite.hash);
      cachedAtom = atom((get): EntityQueryResults<T> => {
        const { data, ...result } = get(atm);

        return {
          ...result,
          entities: data?.map((doc, i) => {
            return createEntity<T>({
              model,
              id: result.snapshot!.docs[i].id!,
              data: doc as T,
            }) as HydratedEntity<T>;
          }),
        };
      });
    }

    atomCache.set(composite.hash, cachedAtom);

    return cachedAtom;
  };

  const createEntityInstance = (
    options: Omit<CreateEntityOptions<T>, "model">,
  ): Entity<T> => {
    return createEntity<T>({ model, ...options });
  };

  const createEntityRecord = (data: PartialEntityRecord<T>): T => {
    const base: EntityRecord = {
      version,
      creatorId: getCreatorId(),
      status: "published",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    return {
      ...getDefaultRecord(),
      ...data,
      ...base,
    } as T;
  };

  const create = async (
    idOrData?: string | PartialEntityRecord<T>,
    data?: PartialEntityRecord<T>,
  ): Promise<Entity<T>> => {
    let id: string | undefined;
    if (typeof idOrData === "string") {
      id = idOrData;
    } else {
      data = idOrData;
    }

    const record = createEntityRecord((data ?? {}) as PartialEntityRecord<T>);

    if (!id) {
      const docRef = await addDoc(collectionRef, record);
      id = docRef.id;
    } else {
      await setDoc(doc(firestore, collectionPath, id), record);
    }

    return createEntityInstance({
      id,
      data: record,
    });
  };

  const model = {
    getBaseQueryComposite,
    getEntities,
    getObserveEntitiesAtom,
    labels,
    collectionPath: options.collectionPath,
    collectionRef,
    create,
    createEntityInstance,
    createEntityRecord,
  } satisfies Model<T>;

  return model;
};

export const userIsCreatorConstraint = () => {
  const id = auth.currentUser ? getUserDefaultRecordId() : "UnknownModelUser";

  return [
    where("creatorId", "==", id),
    "creatorId==" + id,
  ] as CombinedQueryConstraint;
};
